import { QueryCache, MutationCache, QueryClient } from '@tanstack/react-query';

// Duck-typed status check: the app throws TWO different ApiError classes —
// src/types/api.ts (hand-rolled apiClient) and src/api/generated/core/ApiError.ts
// (generated services). Both expose a numeric `status`, so match on that instead
// of `instanceof` (which only matched one of them, letting generated 401s slip
// through — retried 3× and never handled).
const statusOf = (error: unknown): number | undefined =>
  (error as { status?: number } | null | undefined)?.status;

// A burst of concurrent queries all 401 at once when a session dies; only kick
// off the recovery flow once. AuthContext's `auth:logout` listener decides
// whether to restore a stashed admin session (impersonation expired) or send the
// user to /login.
let handlingAuthFailure = false;
const onAuthError = (): void => {
  if (handlingAuthFailure) return;
  handlingAuthFailure = true;
  window.dispatchEvent(new CustomEvent('auth:logout'));
  setTimeout(() => {
    handlingAuthFailure = false;
  }, 3000);
};

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error: unknown) => {
      if (statusOf(error) === 401) onAuthError();
    },
  }),
  mutationCache: new MutationCache({
    onError: (error: unknown) => {
      if (statusOf(error) === 401) onAuthError();
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: (failureCount, error: unknown) => {
        const status = statusOf(error);
        // Never retry auth/permission errors — a retry can't fix them and only
        // delays session restore / re-login.
        if (status === 401 || status === 403) return false;
        if (status !== undefined && status >= 400 && status < 500) return false;
        return failureCount < 3;
      },
      refetchOnWindowFocus: false,
      refetchOnMount: true,
    },
    mutations: {
      onError: (error: unknown) => {
        console.error('Mutation error:', error);
        // Per-component onError provides specific user feedback.
      },
    },
  },
});
